/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UnitService } from '@src/app/modules/unit/unit.service';
import { STD_ANIMATION } from '@src/app/shared/std-animation';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { ActivatedRoute, Router } from '@angular/router';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { Field } from '@src/app/shared/field/Field';
import { UnitTypeService } from '@src/app/modules/unit/unit-type-page/unit-type.service';
import { AreaService } from '@src/app/modules/unit/area.service';
import { UnitCategoryService } from '@src/app/modules/unit/unit-type-page/unit-category.service';
import { GridField } from '@src/app/shared/grid/grid-field';
import { PersonUnitRole } from '@src/app/model/person-unit-role';
import { Unit } from '@src/app/model/unit';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DialogOptions } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { Person } from '@src/app/model/person';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { AppPicklistControl, FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormCheckboxComponent } from '@src/app/shared/form/form-checkbox/form-checkbox.component';
import { numericRange, required } from '@src/app/shared/validators';
import { UnitType } from '@src/app/model/unit-type';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { Txn } from '@src/app/model/txn';
import { Frequency } from '@src/app/model/frequency';
import { CycleService } from '@src/app/modules/budget/cycle.service';
import { Cycle } from '@src/app/model/cycle';
import { DateHelper } from '@src/app/shared/dateHelper';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { PayzService } from '@src/app/modules/txn/payzService';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { PersonPageComponent } from '@src/app/pages/person-page/person-page.component';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { ActionHolder } from '@src/app/shared/form/form.component';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { Role } from '@src/app/model/role';
import { RoleService } from '@src/app/pages/role-page/role.service';
import { ActionGridComponent } from '@src/app/shared/action/action-grid.component';
import { AddActionFormAction } from '@src/app/shared/action/add-action-form-action';
import { MemoField } from '@src/app/shared/memos/MemoField';
import { AddMemoAction } from '@src/app/modules/crm/service-request-page/resolve-request/ResolveRequestAction';
import { MessageService, MsgDef } from '@src/app/shared/message.service';
import { StatementPDFAction } from '@src/app/modules/unit/unit-page/statement-pdf-action';
import { UnitRoleSorter } from '@src/app/modules/unit/unit-page/unit-role-sorter';
import { Area } from '@src/app/model/area';
import { AttachmentGridComponent } from '@src/app/shared/form/file/attachment-grid/attachment-grid.component';
import { FormPhoneComponent } from '@src/app/shared/form/form-phone/form-phone.component';
import { UnitExternalViewAction } from '@src/app/modules/unit/unit-page/unit-external-view-action';
import { ButtonField } from '@src/app/shared/field/ButtonField';
import { NewUnitRequestAction } from '@src/app/modules/unit/unit-page/NewUnitRequestAction';
import { OwnerChangeAction } from '@src/app/modules/unit/unit-page/owner-change-action';
import { AddContactAction } from '@src/app/modules/unit/unit-page/add-contact-action';
import { FieldSetDialogComponent, FieldSetDialogOptions } from '@src/app/shared/dialogs/field-set-dialog/field-set-dialog.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { SchedulePageNavRoute } from '@src/app/routes/schedule-page-nav';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { MyUnitPageNavRoute } from '@src/app/routes/app-router';

@Component({
    selector: 'app-unit-page',
    templateUrl: './unit-page.component.html',
    styleUrls: ['./unit-page.component.scss'],
    animations: [STD_ANIMATION],
    imports: [FormPageComponent]
})

export class UnitPageComponent extends AbstractPageComponent implements OnInit {
    dataSvc = inject(UnitService);
    private router = inject(Router);
    private currentUserSvc = inject(CurrentUserService);
    typeService = inject(UnitTypeService);
    areaService = inject(AreaService);
    categoriesService = inject(UnitCategoryService);
    roleService = inject(RoleService);
    personSvc = inject(PersonService);
    protected payzService = inject(PayzService);
    private activeRoute = inject(ActivatedRoute);
    dialog = inject(MatDialog);
    private msgSvc = inject(MessageService);
    protected http = inject(HttpClient);
    protected cds = inject(ConfirmDialogService);

    dialogRef = inject<MatDialogRef<UnitPageComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA, { optional: true });


    forceTeamId : uuid = null;
    currentCycle: Cycle;
    mineOnly = true;
    roleList: Role[] = [];
    unitRoleList: Role[] = [];

    actionGrid = ActionGridComponent.make('Unit');

    editPerson(pur: PersonUnitRole, cell: AppFormControl) {
        const popDialogRef = this.dialog.open(PersonPageComponent,
            {
                data: {
                    id: pur.personId,
                    forceTeamId: pur.teamId,
                }
            }
        );
        popDialogRef.afterClosed().subscribe(person => {
            if (person) {
                pur.person = person;
                cell.getRow().refreshFrom(pur);
            }
        })
    }

    saveRoleDetails(e: PersonUnitRole) {
        return this.dataSvc.updateRole(e)
    }

    editRole(pur: PersonUnitRole) {
        const fieldSet = new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.idHolder('unitId'),
                FieldMaker.idHolder('teamId'),
                FormPicklistComponent.make('Role', 'roleId', 'role',
                    { items: this.unitRoleList, refreshes: ['roleId'] },
                    { cellOpts: { minWidth: '10em' }, validators: [required] },
                ),
                FormDateComponent.make('From', 'startDate'),
                FormDateComponent.make('To', 'endDate'),
                FormTextAreaComponent.make('Notes', 'notes')

            ],
            formLayout: LAYOUT_OPTIONS.simpleDialog
        })
        fieldSet.setValue(pur)
        const data = {
            fieldSet,
            saveFn: this.saveRoleDetails.bind(this),
            saveText: $localize`Save`,
            title: $localize`${pur.person.fullName} in ${this.page.form.focusItem.name}`,
        } as FieldSetDialogOptions

        this.dialog.open(FieldSetDialogComponent, { data }).afterClosed().subscribe(update => {
            console.log(update);
            if (update) { // Full reloads - the ownerOccupier might have changed.
                this.page.form.reloadContent(update);
            }
        })
    }

    markAsPrimary(i: PersonUnitRole) {
        console.log({i});
        const title = $localize`Make ${i.person.fullName} primary owner of ${this.page.form.focusItem.name}?`;
        const msg = $localize `Service charges will be addressed to this contact. This contact name will appear in lists. Other contacts will still receive copies of communications for the unit.`;
        this.cds.open(title, msg, () => {
            this.dataSvc.markPrimary(i, this.page.form.focusItem as Unit).subscribe( updated => {
                if (updated) {
                    this.page.form.reloadContent(updated);
                }
            })
        })
    }

    memoField = new MemoField(this.dataSvc);

    attachmentGrid = AttachmentGridComponent.make(this.dataSvc);

    calcIndicatorField(f: ButtonField, pur: PersonUnitRole) {
        const futureMember = pur.startDate && !DateHelper.beforeToday(pur.startDate);
        const pastByDate = pur.endDate && DateHelper.beforeToday(pur.endDate);
        const currentByDate = !futureMember && !pastByDate;
        const ownerChangeCurrent = !pur.ownerChangeId;
        const mismatch = !futureMember && ownerChangeCurrent !== currentByDate;

        if (futureMember) {
            f.btnOpts.iconStyle = 'font-size: 24px; color:orange; padding-left:0px';
            f.toolTip = $localize`This contact will have future involvement with the unit`;
            return 'hourglass_top';
        } else if (mismatch) {
            f.btnOpts.iconStyle = 'font-size: 24px; color:red; padding-left:0px';
            f.toolTip = $localize`Warning: ownership record conflicts with start/end dates`;
            return 'warning';
        } else if (!ownerChangeCurrent) {
            f.btnOpts.iconStyle = 'font-size: 24px; color:red; padding-left:0px';
            f.toolTip = $localize`This contact had a past involvement with the unit`;
            return 'block';
        } else {
            f.btnOpts.iconStyle = 'font-size: 24px; color:green; padding-left:0px';
            f.toolTip = $localize`This contact has current involvement with the unit`;
            return 'check';
        }
    }

    currentPersonRow(pur: PersonUnitRole) {
        const row = [];
        return this.personRow(pur,row, true);
    }

    pastPersonRow(pur: PersonUnitRole) {
        return this.personRow(pur, [], true);
    }

    personRow(pur: PersonUnitRole, row: Field[], current: boolean) {

        if (current) {
            row.push(FormButtonComponent.make(' ', 'primaryOwner', {
                type: 'icon', sendServer: false, label: 'Matched', visible: Field.noForm, disable: true,
                cellOpts: { maxWidth: '48px' },
                btnOpts: { iconStyle: 'color:gainsboro', clickMethod: () => this.markAsPrimary(pur) },
                calculateValue: (o: PersonUnitRole, f: ButtonField) => {
                    if (o.personId === (this.page.form.focusItem as Unit).ownerId) {
                        f.toolTip = $localize`This is the primary owner contact for the unit.`;
                        f.btnOpts.iconStyle = 'color:green;';
                        return 'toggle_on'
                    } else {
                        if (o.role && (o.role.globalRoleId === Role.ROLE.OWNER.id || o.role.globalRoleId === Role.ROLE.OWNER_OCCUPIER.id)) {
                            f.enableControl();
                            f.toolTip = $localize`Click here to make this contact primary owner of the unit`;
                            return 'toggle_off';
                        }
                    }
                }
            }));
        }

        row.push(FormButtonComponent.makeTextButton('Role', 'role.name', () => {
            this.editRole(pur);
        }));
        row.push(FormButtonComponent.make('', '', {
            name: 'statusField', type: 'icon', sendServer: false, label: '', readonly: true,
            cellOpts: { heading: '', width: '1%', style: 'padding: 0px 0px 0px 0px; ' },
            calculateValue: (o: AbstractObject, f: ButtonField) => this.calcIndicatorField(f, pur),
        }));

        row.push(FormButtonComponent.makeTextButton('Person Name', 'person.fullName', (cell) => {
            this.editPerson(pur, cell);
        }, {
            readonly: true,
        }));

        row.push(FormTextComponent.make('Phone', 'person.phone', { readonly: true, sendServer: false }));
        row.push(FormTextComponent.make('Email', 'person.email', { readonly: true, sendServer: false }));

        row.push(FormDateComponent.make('From', 'startDate', { cellOpts: { width: '2%' }, readonly: true }));
        row.push(FormDateComponent.make('Until', 'endDate', { cellOpts: { width: '2%' }, readonly: true }));
        row.push(FormTextComponent.make('Address', 'person.address', { readonly: true, sendServer: false, visible: Field.noShow }));
        row.push(FieldMaker.notes().setReadonly(true));
        //row.push(FieldMaker.deleteGridRow()); No delete for now



        return row;
    }

    currentPeopleGrid: GridField = new GridField(
        {
            field: { value: 'people', cellOpts: { heading: 'Contacts' }, sendServer: false, formColumn: 1, formRow: 2, visible: Field.formOnly },
            rowFactory: this.currentPersonRow.bind(this),
        });

    pastPeopleGrid: GridField = new GridField(
        {
            field: {
                value: 'pastPeople', cellOpts: { heading: 'Past Contacts' },
                formColumn: 1, formRow: 2, visible: Field.formOnly, readonly : true, sendServer: false,
            },
            rowFactory: this.pastPersonRow.bind(this),
        });

    portionGrid: GridField = new GridField(
        {
            field: { value: 'portions', cellOpts: { heading: 'Lease Portions' }, formRow: 2, sendServer: false, visible: Field.formOnly, },
            rowFactory: () => [
                FormNavLinkComponent.makeDetail('Schedule', 'schedule.name', 'scheduleId', SchedulePageNavRoute),
                FormNumberComponent.make('Portion', 'portion', {}, { cellOpts: { style: 'text-align: center' }, disable: true }),
                FormNumberComponent.make('Percent', 'percent', { format: 'percent', formatParms: '2.3-3' },
                    { cellOpts: { width: '80px', style: 'text-align: center' }, disable: true }
                ),
                FieldMaker.spacer(),
            ],
        });

    transactionGrid: GridField = new GridField(
        {
            field: {
                readonly: true, value: 'txnWithPayments', sendServer: false, visible: Field.formOnly,
                cellOpts: { heading: 'Transactions' }, tabIcon: 'euro_symbol'
            },
            rowFactory: () => [
                FormTextComponent.make('accordionTitle', 'title', {
                    visible: Field.noShow,
                    calculateValue: (o: Txn) => {
                        if (o.typeId === Txn.TYPE.BANK_IN_ALLOC.id || o.typeId === Txn.TYPE.BANK_IN_ON_ACCT.id) {
                            return `${o.type.name} ${o.parentRefNr} (${o.txnDate})`;
                        } else {
                            return `${o.type.name} ${o.refNr} (${o.txnDate})`;
                        }
                    }
                }),
                FormNavLinkComponent.makeTxnLink('Ref'),
                FormPicklistComponent.make('Type', 'typeId', 'type', { items: Txn.TYPES },
                    { readonly: true, cellOpts: { width: '10em' }, sendServer: false }
                ),

                FormDateComponent.make('Date', 'txnDate', { readonly: true, cellOpts: { width: '2%' }, footer: { text: 'Totals' } }),
                FormNumberComponent.make('Debit', 'debit',
                    { format: 'currency', width: 9, formatParms: '1.2-2' }, { readonly: true }
                ),
                FormNumberComponent.make('Credit', 'credit', { format: 'currency', width: 9, formatParms: '1.2-2' }, { readonly: true }),
                FormNumberComponent.make('Outstanding', 'outstanding', { format: 'currency', width: 9, formatParms: '1.2-2' }, { readonly: true }),
                FormNumberComponent.make('Running Balance', 'balance',
                    { format: 'currency', width: 9, formatParms: '1.2-2' }, { readonly: true, footer: { style: 'opacity: 0%' } }),
                FormTextAreaComponent.make('Notes', 'notes', { readonly: true }),
                FormButtonComponent.make(' ', 'ownerChangeId', {
                    type: 'icon', sendServer: false, label: 'Matched', visible: Field.noForm, disable: true,
                    cellOpts: {maxWidth: '32px', style: 'padding-right: 10px'},
                    btnOpts: {iconStyle: 'color:red'},
                    hint: $localize `Red warnings indicate transactions that are not visible to current owner`,
                    calculateValue: (o: Txn, f: ButtonField) => {
                        if (o.ownerChangeId) {
                            f.toolTip = $localize `Previous owner transaction, not visible to current owner(s)`;
                            return 'warning'
                        }
                        return '';
                    }
                }),
            ],
            hasFooter: true,

        });

    requestGrid = new GridField({
        field:
            { label: 'Requests', value: 'requests', sendServer: false, tabIcon: 'live_help' },
        rowFactory: () => [
            FormNavLinkComponent.makeDetail('Ref', 'refNr', 'id', ServiceRequestPageNavRoute),
            FormTextComponent.make('Title', 'title', { readonly: true }),
            FormTextAreaComponent.make('Description', 'description', { readonly: true }),
            ServiceRequest.getStatusField(),
            ServiceRequest.getCreatedField(),
            ServiceRequest.getLastUpdatedField(),
        ]
    });

    currentChargesField = FormNumberComponent.makeCurrency('This Years Charge', 'currentCharges', {condensed: true, formColumn: 5,
        hint: $localize `The total charges on this account for the year`, visible: Field.formOnly
    });
    arrearsField = FormNumberComponent.makeCurrency('Arrears', 'arrears', {condensed: true, formColumn: 5,
        hint: $localize `Total amount due from previous financial year`, visible: Field.formOnly
    });
    currentPaysField = FormNumberComponent.makeCurrency('Total paid this year', 'currentPays', {condensed: true, formColumn: 5,
        hint: $localize `Total value of all payments received in this finansial year`, visible: Field.formOnly
    });
    discountedOutstandingField = FormNumberComponent.makeCurrency('Discounted Outstanding', 'fullPayAmount', {condensed: true, formColumn: 5,
        hint: $localize `Total outstanding if paid in time to take advantage of any available discounts`, visible: Field.formOnly
    });
    discountField = FormNumberComponent.makePercent('Discount Available', 'fullPayDiscount', {condensed: true, formColumn: 5,
        hint: $localize `Available discount for on time payment`, visible: Field.formOnly
    });
    outstandingField = FormNumberComponent.makeCurrency('Total outstanding', 'outstanding', {condensed: true, total: true, formColumn: 5,
        hint: $localize `Total amount outstanding for this unit`, visible: Field.formOnly
    });
    paymentPlanField = FormNumberComponent.make('Payments Per Year', 'paymentsPerAnnum',{format: 'plain', formatParms: '1-0-0'},
        {condensed: true, formColumn: 5, hint: $localize `Number of payments accepted during the year`, visible: Field.formOnly});

    overdueField = FormNumberComponent.makeCurrency('Plan Overdue', 'overdue', {condensed: true, formColumn: 5, total: true, visible: Field.formOnly,
        hint: $localize `The amount that should have been paid so far this year on the payment plan, less the amount that was actually paid so far`
    });
    oldPayField = FormNumberComponent.makeCurrency('Minimum Payment', 'oldPay', {condensed: true, formColumn: 5, visible: Field.formOnly,
        hint: $localize `The amount required as a regular payment to clear this years charges plus arrears over the course of the year`
    });
    newPayField = FormNumberComponent.makeCurrency('Required Payments', 'newPay', {condensed: true, formColumn: 5, visible: Field.formOnly,
        hint: $localize `The amount required as a regular payment to clear the outstanding amount by the end of the year`
    });
    remainingPaymentsField = FormNumberComponent.make('Remaining Payments', 'remainingPayments',{format: 'plain', formatParms: '1-0-0'},
        {condensed: true, formColumn: 5, hint: $localize `The number of payments left to be made in the current calendar year`, visible: Field.formOnly});

    ownerField = FormTextComponent.make('ownerId', 'ownerId', { visible: Field.noShow });
    idField = FieldMaker.id();

    typeField = FormPicklistComponent.make('Type', 'typeId', 'type',
        { items: [], refreshes: ['categoryId'] }, { formColumn: 2 });

    categoryField = FormPicklistComponent.make('Category', 'categoryId', 'category', { items: [] }, {
        formColumn: 2,
        refresh: (o: UnitType, control: AppPicklistControl) => {
            if (o) {
                control.field.setPicklistItems(o.categories);
                if (control.value && !o.categories.find(c => c.id === control.value)) {
                    control.setValue(null);
                }
            }
        }
    });

    areaField = FormPicklistComponent.make('Area', 'areaId', 'area',
        { refreshes: ['coreId'], items: [] }, { formColumn: 2 });
    coreField = FormPicklistComponent.make('Core', 'coreId', 'core', { items: [] }, {
        formColumn: 2,
        refresh: (o: Area, control: AppPicklistControl) => {
            if (o) {
                control.field.setPicklistItems(o.cores);
                if (control.value && !o.cores.find(c => c.id === control.value)) {
                    control.setValue(null);
                }
            } }
    });

    externalNavField = FormNavLinkComponent.makeDetail('Ext', 'navField', 'id',
        MyUnitPageNavRoute, true).override({
            calculateValue: () => 'View',
            visible: Field.noForm,
    });

    ownerNameField = FormPhoneComponent.make('Owner', 'ownerName', {
        calculateValue: (u: Unit) => u?.owner?.fullName, visible: Field.noForm
    });

    ownerPhoneField = FormPhoneComponent.make('Owner Phone', 'ownerPhone', {
        calculateValue: (u: Unit) => u?.owner?.phone, visible: Field.noForm
    });
    ownerEmailField = FormPhoneComponent.make('Owner Email', 'ownerEmail', {
        calculateValue: (u: Unit) => u?.owner?.email, visible: Field.noForm
    });
    billingAddressOkField = FormButtonComponent.make('Billing Address OK', 'billingAddressOK', {
        type: 'icon', sendServer: false, visible: Field.noForm,
        tableIconStyler: (u:Unit) => (!u.ownerOccupied && !u.owner?.address) ? {'color':'red'} : {'color':'green'},
         
        calculateValue: (u: Unit) => ((!u.ownerOccupied && !u.owner?.address) ? 'close' : 'check')
    });
    billingAddressField = FormPhoneComponent.make('Billing Address', 'billingAddress', {
        calculateValue: (u: Unit) => u?.ownerOccupied ? u.address : u.owner?.address, visible: Field.noShow
    });

    bedroomField = FormNumberComponent.make('Bedroom', 'bedrooms', {}, { formColumn: 3, validators: [numericRange(0, 10)] });
    sizeField = FormNumberComponent.make('Size', 'size', { }, {
        formColumn: 3, validators: [numericRange(0, 99999)]
    });

    floorField = FormNumberComponent.make('Floor', 'floor', {}, {
        formColumn: 3, validators: [numericRange(0, 50)]
    });
    numberCarSpacesField =
        FormNumberComponent.make('Number of car spaces', 'numberCarSpaces',
            { format: 'decimal', formatParms: '1.0-0' },
            { formColumn: 3 }
    );

    frequencyField = FormPicklistComponent.make('Payment Frequency', 'frequencyId', 'frequency',
            { items: Frequency.PAYMENT_OPTIONS}, { formColumn: 4 });
    arrearsStageField = FormPicklistComponent.make('Arrears Stage', 'arrearsStageId', null, { items: Unit.ArrearsStages}, { formColumn: 4 });
    firesSafetyField = FormDateComponent.make('Fire Safety Checked', 'fireSafetyCheck', { formColumn: 4 });
    carSpacesField = FormTextComponent.make('Car Space(s)', 'carSpaces', { formColumn: 4 });

    ownerDetailsField = FormTextAreaComponent.make('Owner Details', 'ownerDetails', {
            readonly: true, sendServer: false, visible: Field.formOnly, formColumn: 1 });

    ownerOccupiedField = FormCheckboxComponent.make('Owner Occupied', 'ownerOccupied', {disable: true, visible: Field.noShow});

    fields: Field[] = [
        FormTextComponent.make('Keyword Search', 'keyword', { visible: Field.noShow, sendServer: false }),
        this.idField,
        FieldMaker.rev(),
        FieldMaker.nameControl({ validators: [required] }),
        FormTextComponent.make('Address', 'address'),
        this.externalNavField,
        this.ownerField,
        this.ownerNameField,
        this.ownerPhoneField,
        this.ownerEmailField,
        this.billingAddressOkField,
        this.billingAddressField,
        FormPicklistComponent.make('Missing Details', 'missingDetails', null,
            { items: [{id: 1, name: 'Yes'}] }, { visible: Field.noShow }),
        this.ownerOccupiedField,
        this.ownerDetailsField,

        this.typeField,
        this.categoryField,

        this.areaField,
        this.coreField,
        this.bedroomField,
        this.sizeField,
        this.floorField,
        this.numberCarSpacesField,

        this.frequencyField,
        this.arrearsStageField,
        this.firesSafetyField,
        this.carSpacesField,

        this.currentChargesField, this.arrearsField, this.currentPaysField, this.discountField, this.discountedOutstandingField,
        this.outstandingField, this.paymentPlanField, this.oldPayField,
        this.overdueField, this.remainingPaymentsField, this.newPayField,

        this.currentPeopleGrid,
        this.portionGrid,
        this.pastPeopleGrid
    ];

    addActions = new ActionHolder({name: 'Add', tipText: $localize`Add to this unit`})
        .add(new NewUnitRequestAction())
        .add(new AddActionFormAction(this.actionGrid))
        .add(new AddActionFormAction(this.actionGrid, 'past'))
        .add(new AddMemoAction(this.memoField))
        .add(new AddContactAction())
        .add(new OwnerChangeAction())
    ;

    config = new FormConfig({
        navRoute: UnitPageNavRoute,
        docPath: 'omc-community/managers/units',
        title: $localize`Units`,
        help: $localize`The units that make up this multi unit development`,
        fieldSet: new FieldSet({
            fields: this.fields,
            formLayout: [
                { cells: [{ width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }] },
                { cells: [{ colspan: 5, pageTab: 'yes' }] }
            ]
        }),
        service: this.dataSvc,
        mode: 'list',
        actions: [
            this.addActions, new StatementPDFAction(), new UnitExternalViewAction()
        ],
        objectFactory: this.beforeNew.bind(this),
        beforeEdit: this.beforeEdit.bind(this),
        configReady: this.configReady,
        setTeam: this.setTeam.bind(this),
    });

    constructor() {
        const cycleSvc = inject(CycleService);


        super();
        const activeRoute = this.activeRoute;

        if (activeRoute.snapshot.url[0]?.path === 'myunits') {
            //this.mineOnly = true;
            //this.dataSvc = this.myUnitSvc;
            //this.config.navRoute = MyUnitPageNavRoute;
            //this.config.service = this.myUnitSvc;
            //this.config.allowNew = false;
            //this.config.allowEdit = false;
            //this.config.tabFields = [this.transactionGrid];
            console.error('Should not be in here any more... there is a new myUnitPage');
        } else {
            this.mineOnly = false;
            this.config.tabFields = [this.actionGrid, this.memoField, this.requestGrid, this.transactionGrid, this.attachmentGrid];
            this.config.navRoute = UnitPageNavRoute;
        }

        cycleSvc.get<Cycle>(true).subscribe((cycles) => {
            this.currentCycle = cycleSvc.getCurrentCycle(cycles);
            this.configReady.next(null);
        })
    }

    setTeam(teamId: uuid) {
        this.forceTeamId = teamId;

        this.typeService.get(false, null, teamId).subscribe(types => {
            this.typeField.setPicklistItems(types);
        });
        this.categoriesService.get(false, null, teamId).subscribe(cats => {
            this.categoryField.setPicklistItems(cats);
        });
        this.areaService.get<Area>(false, null, teamId).subscribe(areas => {
            this.areaField.setPicklistItems(areas);
            this.coreField.setPicklistItems(this.areaService.getCores(areas));
        });
        this.roleService.get(false, null, teamId).subscribe(roles => {
            this.roleList.length = 0;
            this.unitRoleList.length = 0;
            roles.forEach((r: Role) => {
                this.roleList.push(r);
                if (r.unitLinked) {
                    this.unitRoleList.push(r);
                }
            });
        });
        if (this.dialogRef && this.dialogOptions?.condensed) {
            this.currentPaysField.visible = Field.noShow;
            this.areaField.visible = Field.noShow;
            this.coreField.visible = Field.noShow;
            this.sizeField.visible = Field.noShow;
            this.floorField.visible = Field.noShow;
            this.firesSafetyField.visible = Field.noShow;
        }
    }

    private destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.currentUserSvc.getCurrentUser().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(u => {

            this.currentUser = u;

            if (this.mineOnly) {
                if (!this.activeRoute.snapshot.params.itemId) {
                    if (this.currentUser.activePositions.length === 1) {
                        this.router.navigate([MyUnitPageNavRoute.getIdUrl(this.currentUser.activePositions[0].unitId)]);
                    }
                }
            }
        })
    }
/*
    frequencyChanges(newFrequency: Frequency) {
        //let unit = this.page.form.focusItem as Unit
        //unit.frequencyId =
        //unit.frequency = fld.picklist.valueObject as Frequency;

        const unit = JSON.parse(JSON.stringify(this.page.form.focusItem)) as Unit;
        unit.frequencyId = newFrequency.id;
        unit.frequency = newFrequency;

        this.calculatePaymentSchedule(unit);

        this.page.form.focusItem = unit;

        //this.yearStartField.control.setValue(NrHelper.toFormattedCurrency(unit.yearStart), { emitEvent: false });
        //this.currentPaysField.control.setValue(NrHelper.toFormattedCurrency(unit.currentPays), { emitEvent: false });
        //this.outstandingField.control.setValue(NrHelper.toFormattedCurrency(unit.outstanding), { emitEvent: false });
        //this.newPayField.control.setValue(NrHelper.toFormattedCurrency(unit.newPay), { emitEvent: false });
        //this.overdueField.control.setValue(NrHelper.toFormattedCurrency(unit.overdue), { emitEvent: false });
        //this.oldPayField.control.setValue(NrHelper.toFormattedCurrency(unit.oldPay), { emitEvent: false });

    }
*/
    beforeNew(): Observable<Unit> {
        /*
        this.currentChargesField.visible = Field.noShow;
        this.arrearsField.visible = Field.noShow;
        this.currentPaysField.visible = Field.noShow;
        this.outstandingField.visible = Field.noShow;
        this.newPayField.visible = Field.noShow;
        this.overdueField.visible = Field.noShow;
        this.oldPayField.visible = Field.noShow;
        */
        const unit = new Unit();

        return of(unit);
    }

    isCurrent(o: PersonUnitRole) {
        if ((o === null) || o === undefined) {
            return $localize`Unit Owner not listed in unit people, please select a main owner in the list`
        } else if (o.startDate && !DateHelper.beforeToday(o.startDate)) {
            return $localize`Unit Owner selected has not taken ownership of unit yet. If you need to billout before the new owner takes ownership, they will still receive the bill`
        } else if ((o.endDate && DateHelper.beforeToday(o.endDate))) {
            return $localize`Unit Owner selected is no longer listed as owner, you should update the main owner`
        } else {
            return null;
        }
    }
    checkOwner(o: Unit) {

        const oPerson = o.people.find(p => p.personId === o.ownerId);
        const status = this.isCurrent(oPerson);

        if (status !== null) {
            this.msgSvc.show(new MsgDef(status, 'warn'));
        } else {
            if ( oPerson.role.globalRoleId === Role.ROLE.OWNER.id) {
                if (!oPerson.person?.address || (oPerson.person?.address && oPerson.person.address.indexOf("\n") < 0)) {
                    const msg = `Owners are non resident - You need to either mark ${oPerson.person?.fullName} as an Owner Occupier,
                    or click on them and add a mailing address`
                    this.msgSvc.show(new MsgDef(msg, 'warn'));
                }
            }
        }
    }

    beforeEdit(o: Unit): Unit {
        if (o === null) {
            return new Unit();
        }

        o.pastPeople = o.people.filter(p => !p.isCurrent);
        o.people = o.people.filter(p => p.isCurrent);

        this.checkOwner(o);
        if (o.owner) {
            if (o.ownerOccupied) {
                o.ownerDetails = $localize `${o.owner.fullName}\nOWNER OCCUPIED`;
            } else if (o.owner.address){
                o.ownerDetails = $localize `${o.owner.fullName}\n${o.owner.address}`;
            } else {
                o.ownerDetails = $localize `${o.owner.fullName}\n*** No Address ***`;
            }

        } else {
            o.owner = {address: 'OWNER DETAILS MISSING'} as Person
        }

        o.people = o.people.sort(new UnitRoleSorter().sort);
        o['txnWithPayments'] = Unit.sortUnitTransactions(o.transactions);

        return this.calculatePaymentSchedule(o);

    }

    private getKey(t: Txn) {
        return t.txnDate + t.id;
    }

    calculatePayments(): {expected: number, remaining: number} {
        // Parse the YYYY-MM-DD date strings to get year, month, and day
        const c = this.currentCycle;

        const firstPaymentDate = (c.firstPayments > c.from) ? c.firstPayments : c.from;
        const lastPaymentDate = (c.lastPayments && c.lastPayments < c.to) ? c.lastPayments : c.to;
        const firstYear = parseInt(firstPaymentDate.substring(0, 4), 10);
        const firstMonth = parseInt(firstPaymentDate.substring(5, 7), 10);
        const firstDay = parseInt(firstPaymentDate.substring(8, 10), 10);

        const lastYear = parseInt(lastPaymentDate.substring(0, 4), 10);
        const lastMonth = parseInt(lastPaymentDate.substring(5, 7), 10);
        const lastDay = parseInt(lastPaymentDate.substring(8, 10), 10);

        // Calculate the base number of payments based on the month difference
        let expected = (lastYear - firstYear) * 12 + (lastMonth - firstMonth) + 1;

        // Adjust the count if the end date's day is before the payment day of its month
        if (lastDay < firstDay) {
            expected--;
        }
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1; // getMonth() is 0-indexed
        const currentDay = currentDate.getDate();

        // Calculate number of payments should already be made
        let paymentsMade = (currentYear - firstYear) * 12 + (currentMonth - firstMonth) + 1;
        if (currentDay < firstDay) {
            paymentsMade--;
        }

        // Ensure paymentsMade is not negative or greater than expectedPayments
        paymentsMade = Math.max(0, paymentsMade);
        paymentsMade = Math.min(expected, paymentsMade);

        const remaining = expected - paymentsMade;

        return {expected, remaining};
    }

    calculatePaymentSchedule(o: Unit) {
        let outstanding = 0;
        let currentPays = 0;
        let currentCharges = 0;
        let arrears = 0;

        for (const t of o.transactions) {
            outstanding += t.outstanding;
            if (t.typeId === Txn.TYPE.BANK_IN_ALLOC.id || t.typeId === Txn.TYPE.BANK_IN_ON_ACCT.id) {
                if (t.txnDate >= this.currentCycle.from) {
                    currentPays += t.credit;
                }
                if (t.txnDate <= this.currentCycle.from) { // Unallocated credits from previous years reduces arrears
                    arrears += t.outstanding;
                }
            } else {
                if (t.txnDate >= this.currentCycle.from) {
                    currentCharges += t.debit - t.credit;
                } else {
                    arrears += t.outstanding;
                }
            }
        }

        const pays = this.calculatePayments();
        const paysExpected = pays.expected;
        const paysLeft = pays.remaining;

        o.yearStart = outstanding + currentPays;
        o.oldPay = (currentCharges + arrears)/ pays.expected;
        o.currentPays = currentPays;
        o.currentCharges = currentCharges;
        o.arrears = arrears;
        o.outstanding = outstanding;
        o.overdue = Math.max((o.oldPay * (paysExpected - paysLeft)) - currentPays, 0);
        o.paymentsPerAnnum = paysExpected;
        o.remainingPayments = paysLeft;
        o.newPay = paysLeft ? o.outstanding / paysLeft : 0;
        const discountAvailable = DateHelper.daysFromToday(this.currentCycle.fullPayBy) >= 0;
        if (discountAvailable) {
            o.fullPayDiscount = this.currentCycle.fullPayDiscount;
            this.discountField.hint = $localize `Available discount for payment before ${DateHelper.dateFromISO(this.currentCycle.fullPayBy)}`
        } else {
            o.fullPayDiscount = 0;
            this.discountField.hint = $localize `After ${DateHelper.dateFromISO(this.currentCycle.fullPayBy)} discounts are not available`
        }
        const discountAmt = o.currentCharges * o.fullPayDiscount;
        o.fullPayAmount = Math.max(o.outstanding - discountAmt, 0);
        if (discountAvailable && (this.currentCycle.fullPayBy >= this.currentCycle.lastPayments) ) {
            // Folks on payment plan will qualify for full pay discounts
            o.oldPay = o.oldPay - (discountAmt / paysExpected)
            o.newPay = o.newPay - (discountAmt / paysLeft);
        }

        return o;
    }

    plMapper(itemPicked): PersonUnitRole {
        if (itemPicked) {
            const newItem = new PersonUnitRole();
            newItem.personId = itemPicked.id;
            newItem.person = itemPicked;
            return newItem;
        } else {
            return null;
        }
    }
}

